import { useEffect, useMemo, useRef, useState } from "react";
import { CountdownFormat, computeTimeLeft } from "@cede/utils";

export { computeTimeLeft };

export const useCountdown = (
  date?: Date | string,
  config?: {
    onFinish?: () => void;
  },
) => {
  const [countdown, setCountdown] = useState<CountdownFormat | null>(null);
  const onFinishRef = useRef(config?.onFinish);

  const targetTimestamp = useMemo(() => {
    if (!date) return null;
    const targetDate = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(targetDate.getTime())) {
      return null;
    }
    return targetDate.getTime();
  }, [date]);

  useEffect(() => {
    onFinishRef.current = config?.onFinish;
  }, [config?.onFinish]);

  useEffect(() => {
    if (!targetTimestamp) {
      setCountdown(null);
      return;
    }

    const targetDate = new Date(targetTimestamp);
    let intervalId: ReturnType<typeof setInterval> | null = null;

    const updateCountdown = () => {
      const timeLeft = computeTimeLeft(targetDate);
      setCountdown(timeLeft);

      if (Date.now() >= targetTimestamp) {
        onFinishRef.current?.();
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
      }
    };

    updateCountdown();
    intervalId = setInterval(updateCountdown, 1000);

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [targetTimestamp]);

  return countdown;
};
